<template>
  <form ref="form" action="#" method="post">
    <input type="hidden" name="_token" :value="csrfToken" />
    <div class="l-page l-page--message-edit-condition-confirm">
      <main class="l-main">
        <breadcrumb :breadcrumb-data="breadcrumbData" />

        <div class="l-main-head">
          <div class="c-title u-w420">
            <div class="c-title__text c-title__text--blue u-fs34">配信内容の確認</div>
          </div>
          <div class="c-progress">
            <ul class="c-progress-list">
              <li class="c-progress-list__item">メッセージの作成</li>
              <li class="c-progress-list__item">配信条件の設定</li>
              <li class="c-progress-list__item is-current">配信内容の確認</li>
              <li class="c-progress-list__item">設定完了</li>
            </ul>
          </div>
        </div>

        <!-- 配信内容セクション -->
        <div class="l-main-content">
          <div class="p-message-edit-confirm p-message-edit-confirm--preview">
            <div class="c-title c-title--left">
              <div class="c-title__text u-fs24">配信内容</div>
              <div class="c-text u-fs--s">※プレビューと実際の配信されるメッセージ表示は異なることがあります</div>
            </div>
            <!-- ヘッダー行 -->
            <div class="confirm-preview-header">
              <div class="confirm-preview-header__col">PCでの配信例</div>
              <div class="confirm-preview-header__col">スマホでの配信例</div>
            </div>

            <template v-if="isReminderDelivery">
              <template v-for="(schedule, idx) in message.schedules" :key="'row-' + idx">
                <div

                  class="confirm-preview-row"
                  :class="{ 'confirm-preview-row--last': idx === message.schedules.length - 1 }"
                >
                  <div class="confirm-preview-row__col">
                    <div v-if="idx > 0" class="confirm-preview-separator">
                      ↓ {{ formatMinutesReadable(schedule.send_after_minutes) }}後 ↓
                    </div>
                    <div class="confirm-preview-label">{{ idx + 1 }}通目</div>
                    <message-preview
                      :title="replacePlaceholders(schedule.message_title)"
                      :text="replacePlaceholders(schedule.message_text)"
                      :url="schedule.click_url"
                      :icon-url="getScheduleIconUrl(schedule)"
                      :large-image-url="schedule.large_image_url"
                    />
                  </div>
                  <div class="confirm-preview-row__col">
                    <div v-if="idx > 0" class="confirm-preview-separator">
                      ↓ {{ formatMinutesReadable(schedule.send_after_minutes) }}後 ↓
                    </div>
                    <div class="confirm-preview-label">{{ idx + 1 }}通目</div>
                    <message-preview-sp
                      :title="replacePlaceholders(schedule.message_title)"
                      :text="replacePlaceholders(schedule.message_text)"
                      :url="schedule.click_url"
                      :icon-url="getScheduleIconUrl(schedule)"
                      :large-image-url="schedule.large_image_url"
                      :badge-url="getScheduleBadgeUrl(schedule)"
                    />
                  </div>
                </div>
              </template>
            </template>
            <template v-else>
              <div class="confirm-preview-row confirm-preview-row--last">
                <div class="confirm-preview-row__col">
                  <message-preview
                    :title="previewTitle"
                    :text="previewText"
                    :url="message.click_url"
                    :icon-url="message.icon_url || defaultIconUrl"
                    :large-image-url="message.large_image_url"
                  />
                </div>
                <div class="confirm-preview-row__col">
                  <message-preview-sp
                    :title="previewTitle"
                    :text="previewText"
                    :url="message.click_url"
                    :icon-url="message.icon_url || defaultIconUrl"
                    :large-image-url="message.large_image_url"
                    :badge-url="message.badge_image_url || defaultBadgeUrl"
                  />
                </div>
              </div>
            </template>
            <div
              v-if="placeholders.length > 0 || hasWorkingTime"
              class="p-message-edit-confirm-list"
              style="margin-top: 0px"
            >
              <div v-for="(ph, index) in placeholders" :key="'ph-' + index" class="p-message-edit-confirm-list__item">
                <div class="p-message-edit-confirm__title">差し込みテキスト{{ index + 1 }}</div>
                <div class="p-message-edit-confirm__text">
                  <span v-text="placeholderKeyDisplay(ph)"></span> デフォルトテキスト: {{ ph.default_value || '' }}
                </div>
              </div>
              <div v-if="hasWorkingTime" class="p-message-edit-confirm-list__item">
                <div class="p-message-edit-confirm__title">配信時間帯</div>
                <div class="p-message-edit-confirm__text">
                  <div class="confirm-nw-clock-wrap">
                    <div class="confirm-nw-clock-text">
                      <span class="confirm-nw-clock-time">{{ wtStartFormatted }}</span>
                      <span class="confirm-nw-clock-tilde">〜</span>
                      <span class="confirm-nw-clock-time">{{ wtEndFormatted }}</span>
                    </div>
                    <svg
                      viewBox="0 0 100 100"
                      width="140"
                      height="140"
                      class="confirm-nw-clock"
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="36"
                        fill="none"
                        stroke="#fff"
                        stroke-width="10"
                      />
                      <path
                        :d="workingArcPath"
                        fill="none"
                        stroke="rgba(60,182,210,0.3)"
                        stroke-width="10"
                        stroke-linecap="round"
                      />
                      <template v-for="h in [0, 6, 12, 18]" :key="'h' + h">
                        <text

                          :x="50 + 47 * Math.cos(wtToAngle(h, 0))"
                          :y="50 + 47 * Math.sin(wtToAngle(h, 0))"
                          text-anchor="middle"
                          dominant-baseline="central"
                          font-size="5"
                          :fill="wtInRange(h) ? '#333' : '#94a3b8'"
                          :font-weight="wtInRange(h) ? 600 : 400"
                        >
                          {{ h }}
                        </text>
                      </template>
                      <circle
                        :cx="wtStartPos.x"
                        :cy="wtStartPos.y"
                        r="4.5"
                        fill="#3cb6d2"
                      />
                      <text
                        :x="wtStartPos.x"
                        :y="wtStartPos.y"
                        text-anchor="middle"
                        dominant-baseline="central"
                        font-size="3.5"
                        fill="#fff"
                        font-weight="700"
                      >
                        S
                      </text>
                      <circle
                        :cx="wtEndPos.x"
                        :cy="wtEndPos.y"
                        r="4.5"
                        fill="#3cb6d2"
                      />
                      <text
                        :x="wtEndPos.x"
                        :y="wtEndPos.y"
                        text-anchor="middle"
                        dominant-baseline="central"
                        font-size="3.5"
                        fill="#fff"
                        font-weight="700"
                      >
                        E
                      </text>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 配信条件セクション (v1 RuleRoot サマリ表示) -->
        <div class="l-main-content">
          <div class="p-message-edit-confirm p-message-edit-confirm--details">
            <div class="c-title">
              <div class="c-title__text u-fs24">配信条件</div>
            </div>
            <div class="p-message-edit-confirm-list">
              <div class="p-message-edit-confirm-list__item">
                <div class="p-message-edit-confirm__title">配信予約の設定</div>
                <div class="p-message-edit-confirm__text">{{ triggerLabel }}</div>
              </div>
              <div class="p-message-edit-confirm-list__item">
                <div class="p-message-edit-confirm__title">配信条件の内容</div>
                <div class="p-message-edit-confirm__text">
                  <reminder-condition-summary :condition="condition" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 差し込みテキスト / 差し込み大画像URL 系の注意書き。
             auto : 差し込み系オプションが設定されているときのみ表示 (デフォルト値配信の警告)
             manual: 常に表示 (直下のスクリプトタグ設置を案内) -->
        <div v-if="showPlaceholderNotice" class="l-main-content">
          <div class="placeholder-notice" role="alert">
            <div class="placeholder-notice__icon">!</div>
            <div class="placeholder-notice__body">{{ placeholderNoticeText }}</div>
          </div>
        </div>

        <!-- スクリプトタグセクション (手動トリガー時のみ表示。自動配信は発火に外部スクリプト不要のため非表示) -->
        <div v-if="!isAutoTrigger" class="l-main-content">
          <div class="p-message-edit-confirm p-message-edit-confirm--details">
            <div class="c-title c-title--left">
              <div class="c-title__text u-fs24">スクリプトタグ</div>
              <div class="c-text u-fs--s">
                ※スクリプトの設置方法のマニュアルは <a href="https://manual.webpush.jp/posts/reminder-script" target="_blank" rel="noopener noreferrer">こちら</a> をご参照ください。
              </div>
            </div>
            <div class="p-message-edit-confirm-list">
              <div class="p-message-edit-confirm-list__item script-tag-wrapper">
                <div class="script-tag-block">
                  <div class="script-tag-heading">配信を予約するスクリプト(必須)</div>
                  <div class="script-tag-desc">
                    <p>リマインダー配信を予約するスクリプトです。</p>
                    <p>ブラウザプッシュタグ(pushone.js タグ)の後に設置してください。</p>
                    <p>
                      配信のきっかけとなるユーザー行動(例:
                      フォーム離脱、カート追加など)を検知したタイミングで、このコードを呼び出してください。
                    </p>
                  </div>
                  <div class="script-tag-area">
                    <textarea
                      ref="setTextarea"
                      v-model="scriptTagCode"
                      class="c-textarea__item script-tag-textarea"
                      name="scriptTagCode"
                      readonly
                    ></textarea>
                    <button
                      class="c-btn c-btn--circle script-tag-copy-btn"
                      type="button"
                      @click="onClipClick($event, 'scriptTagCode', 'set')"
                    >
                      <div
                        v-tooltip="{
                          content: copiedTarget === 'set' ? 'コピーしました' : 'クリップボードにコピー',
                          hideTriggers: (triggers) => triggers.filter((trigger) => trigger !== 'click'),
                        }"
                      >
                        <div class="c-icon-circle c-icon-circle--gray-2 u-wh40">
                          <div class="c-icon-circle__item c-icon-circle__item--white c-icon__item--alpha70 u-wh20">
                            <svg
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                            >
                              <use xlink:href="/assets/img/sprite.svg#clipboard" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <div class="script-tag-block">
                  <div class="script-tag-heading">配信を解除するスクリプト(任意)</div>
                  <div class="script-tag-desc">
                    <p>リマインダー配信の解除をするスクリプトです。</p>
                    <p>ブラウザプッシュタグ(pushone.js タグ)の後に設置してください。</p>
                    <p>
                      配信が不要になったタイミング(例:
                      購入完了、会員登録完了、問い合わせ完了など)で、このコードを呼び出してください。
                    </p>
                  </div>
                  <div class="script-tag-area">
                    <textarea
                      v-model="unsetScriptTagCode"
                      class="c-textarea__item script-tag-textarea script-tag-textarea--unset"
                      name="unsetScriptTagCode"
                      readonly
                    ></textarea>
                    <button
                      class="c-btn c-btn--circle script-tag-copy-btn"
                      type="button"
                      @click="onClipClick($event, 'unsetScriptTagCode', 'unset')"
                    >
                      <div
                        v-tooltip="{
                          content: copiedTarget === 'unset' ? 'コピーしました' : 'クリップボードにコピー',
                          hideTriggers: (triggers) => triggers.filter((trigger) => trigger !== 'click'),
                        }"
                      >
                        <div class="c-icon-circle c-icon-circle--gray-2 u-wh40">
                          <div class="c-icon-circle__item c-icon-circle__item--white c-icon__item--alpha70 u-wh20">
                            <svg
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlns:xlink="http://www.w3.org/1999/xlink"
                            >
                              <use xlink:href="/assets/img/sprite.svg#clipboard" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- フッターボタン -->
    <div class="p-message-predict p-message-predict--no-number">
      <div class="p-message-predict__content">
        <div class="p-message-predict-suspend"></div>
        <div class="p-message-predict__btns">
          <button class="c-btn c-btn--gray u-w320 u-h50 u-fs20" type="button" @click="onBackConditionClick">
            <p class="c-btn__text">配信条件設定に戻る</p>
          </button>
          <button
            class="c-btn c-btn--blue u-w320 u-h50 u-fs20"
            :class="{ 'is-loading': isLoadingSaveBtn, 'is-completed': isCompleteMessage }"
            :disabled="isLoadingSaveBtn || isCompleteMessage"
            type="button"
            @click="onDeliveryClick"
          >
            <p class="c-btn__text">配信を開始する</p>
          </button>
        </div>
      </div>
    </div>

    <!-- モーダル: 配信開始前の確認 -->
    <control-modal
      ref="refConfirmDeliveryModal"
      classes="c-modal--common"
      title="配信を開始しますか?"
      body="設定した配信条件に合致したユーザーへ通知が送信されます。開始後も一覧画面から停止できます。"
      cancel-button-label="キャンセル"
      enter-button-label="配信を開始する"
      :is-active="isConfirmDelivery"
      :on-close-click="onCloseConfirmDelivery"
      :on-cancel-click="onCloseConfirmDelivery"
      :on-enter-click="onConfirmDelivery"
    />

    <!-- モーダル: 配信設定完了 -->
    <control-modal
      ref="refCompleteModal"
      classes="c-modal--result"
      :body="completeMessage"
      cancel-button-label="メッセージ一覧へ"
      :can-click-outside="false"
      :is-active="isCompleteMessage"
      :on-close-click="onCloseControlModal"
      :on-cancel-click="onCancelControlModal"
    />

    <!-- モーダル: エラー表示 -->
    <error-modal :is-active="isError" :message="errorMessage" :on-close-click="onCloseErrorModal" />
  </form>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    csrfToken: String,
    confirmInfo: Object,
  },
  data() {
    return {
      completeMessage: '',
      errorMessage: '',
      isCompleteMessage: false,
      isError: false,
      isLoadingSaveBtn: false,
      // 直近にコピーしたボタンを識別する: 'set' | 'unset' | null
      // boolean だと set/unset 双方のツールチップが同時に「コピーしました」になり混乱するため対象を持たせる
      copiedTarget: null,
      // 配信開始ボタン押下後、実際の API 呼び出し前に挟む確認モーダルの開閉状態
      isConfirmDelivery: false,
    };
  },
  computed: {
    message() {
      return this.confirmInfo.message;
    },
    condition() {
      return this.confirmInfo.condition;
    },
    triggerLabel() {
      var t = this.condition && this.condition.trigger_type;
      if (t === 'auto') return '条件に合致したら自動予約';
      if (t === 'manual') return '任意のタイミングで予約';
      return '未設定';
    },
    isAutoTrigger() {
      return this.condition && this.condition.trigger_type === 'auto';
    },
    placeholders() {
      return this.message.placeholders || [];
    },
    // 差し込み大画像URL 機能を有効にしているかどうか (use_large_image_placeholder は 0/1 の整数フラグ)。
    useLargeImagePlaceholder() {
      return Number(this.message.use_large_image_placeholder) === 1;
    },
    // 差し込み系オプション (差し込みテキスト or 差し込み大画像URL) の何れかが設定されているかどうか。
    hasPlaceholderConfig() {
      return this.placeholders.length > 0 || this.useLargeImagePlaceholder;
    },
    // 注意書きノートの表示可否。
    // auto : 差し込み系オプションが設定されているときのみ表示 (デフォルト値配信の警告)
    // manual: 常に表示 (下段スクリプトタグ設置の案内が必須のため)
    showPlaceholderNotice() {
      if (this.isAutoTrigger) return this.hasPlaceholderConfig;
      return true;
    },
    // 上記オプションが設定されている場合に確認画面で出す注意書きテキスト。
    // auto: デフォルト値配信の警告 (差し込みは反映されない)
    // manual: 下段スクリプトタグの設置を案内
    placeholderNoticeText() {
      if (this.isAutoTrigger) {
        if (!this.hasPlaceholderConfig) return '';
        var names = [];
        if (this.placeholders.length > 0) names.push('差し込みテキスト');
        if (this.useLargeImagePlaceholder) names.push('差し込み大画像URL');
        return (
          'メッセージに' +
          names.join('・') +
          'が設定されていますが、「条件に合致したら自動予約」ではデフォルトの値で配信されます。\n' +
          '任意の値で配信するには、「任意のタイミングで予約」に切り替えて、配信予約はスクリプトを設置してご対応ください。'
        );
      }
      return '下記を参考に「任意のタイミングで予約」用のスクリプトの設置をお願い致します。';
    },
    defaultIconUrl() {
      return this.confirmInfo.defaultIconUrl || null;
    },
    defaultBadgeUrl() {
      return this.confirmInfo.defaultBadgeUrl || null;
    },
    isReminderDelivery() {
      return this.message.schedules && this.message.schedules.length > 0;
    },
    previewTitle() {
      var title = this.isReminderDelivery ? this.message.schedules[0].message_title : this.message.message_title;
      return this.replacePlaceholders(title);
    },
    previewText() {
      var text = this.isReminderDelivery ? this.message.schedules[0].message_text : this.message.message_text;
      return this.replacePlaceholders(text);
    },
    showReminderNote() {
      return this.isReminderDelivery && this.message.schedules.length >= 2;
    },
    deliverModeLabel() {
      return this.isReminderDelivery ? 'リマインダー配信' : 'トリガー配信';
    },
    hasWorkingTime() {
      return this.message.working_start_time != null && this.message.working_end_time != null;
    },
    wtStartHour() {
      return Math.floor(this.message.working_start_time / 100);
    },
    wtStartMinute() {
      return this.message.working_start_time % 100;
    },
    wtEndHour() {
      return Math.floor(this.message.working_end_time / 100);
    },
    wtEndMinute() {
      return this.message.working_end_time % 100;
    },
    wtStartFormatted() {
      return String(this.wtStartHour).padStart(2, '0') + ':' + String(this.wtStartMinute).padStart(2, '0');
    },
    wtEndFormatted() {
      return String(this.wtEndHour).padStart(2, '0') + ':' + String(this.wtEndMinute).padStart(2, '0');
    },
    wtStartPos() {
      var a = this.wtToAngle(this.wtStartHour, this.wtStartMinute);
      return { x: 50 + 36 * Math.cos(a), y: 50 + 36 * Math.sin(a) };
    },
    wtEndPos() {
      var a = this.wtToAngle(this.wtEndHour, this.wtEndMinute);
      return { x: 50 + 36 * Math.cos(a), y: 50 + 36 * Math.sin(a) };
    },
    workingArcPath() {
      if (!this.hasWorkingTime) return '';
      var a1 = this.wtToAngle(this.wtStartHour, this.wtStartMinute);
      var a2 = this.wtToAngle(this.wtEndHour, this.wtEndMinute);
      var R = 36,
          CX = 50,
          CY = 50;
      var x1 = CX + R * Math.cos(a1),
          y1 = CY + R * Math.sin(a1);
      var x2 = CX + R * Math.cos(a2),
          y2 = CY + R * Math.sin(a2);
      var diff = this.wtEndHour * 60 + this.wtEndMinute - (this.wtStartHour * 60 + this.wtStartMinute);
      if (diff <= 0) diff += 1440;
      return 'M ' + x1 + ' ' + y1 + ' A ' + R + ' ' + R + ' 0 ' + (diff > 720 ? 1 : 0) + ' 1 ' + x2 + ' ' + y2;
    },
    scriptTagCode() {
      var reminderLabel = this.confirmInfo.reminderLabel;
      var placeholders = this.message.placeholders || [];
      var useLargeImagePlaceholder = Number(this.message.use_large_image_placeholder) === 1;
      var hasOptions = placeholders.length > 0 || useLargeImagePlaceholder;
      var self = this;

      // 非エンジニア担当者が script タグ外にコードを貼り付けて動作しない事例があったため、
      // コピー用テキストは最初から <script> タグで囲んで提供する。
      var lines = [];
      lines.push('<script>');

      if (hasOptions) {
        // 引数 (ラベル + オプションオブジェクト) がある場合は
        // 視認性のため複数行でインデント表示する。
        lines.push('window.coins2b.setReminder(' + this.toJsLiteral(reminderLabel) + ', {');

        if (placeholders.length > 0) {
          lines.push('  // サイトへの設置時に実際の値へ書き換えてください');
          lines.push('  placeholderText: {');
          placeholders.forEach(function (ph, index) {
            var comma = index < placeholders.length - 1 ? ',' : '';
            // キーも値も JS 文字列リテラルとして安全化する (引用符 / 改行 / script 終了タグ混入 への耐性)
            lines.push(
              '    ' +
                self.toJsLiteral(ph.placeholder_key) +
                ': ' +
                self.toJsLiteral(ph.default_value || '') +
                comma
            );
          });
          lines.push('  }' + (useLargeImagePlaceholder ? ',' : ''));
        }

        if (useLargeImagePlaceholder) {
          lines.push('  // サイトへの設置時に実際の値へ書き換えてください');
          lines.push('  // nullの場合には設定された画像が配信されます。');
          lines.push('  placeholderLargeImageUrl: null');
        }

        lines.push('});');
      } else {
        // オプション無し (ラベルのみ) ならコードが短いため 1 行で出力する。
        lines.push('window.coins2b.setReminder(' + self.toJsLiteral(reminderLabel) + ');');
      }

      lines.push('</' + 'script>');
      return lines.join('\n');
    },
    // 配信対象からユーザーを外す unset 用スクリプト。
    // set と同様に script タグで囲み、そのままページへ貼り付ければ動作する形で出力する。
    unsetScriptTagCode() {
      var reminderLabel = this.confirmInfo.reminderLabel;
      var lines = [];
      lines.push('<script>');
      lines.push('window.coins2b.unsetReminder(' + this.toJsLiteral(reminderLabel) + ');');
      lines.push('</' + 'script>');
      return lines.join('\n');
    },
    breadcrumbData() {
      return [
        { title: '配信メッセージの一覧', url: this.confirmInfo.listUrl },
        {
          title: '配信メッセージの編集',
          url: '/coins-admin/reminder/message/add?type=edit&message_id=' + this.confirmInfo.messageId,
        },
        { title: '配信条件の設定', url: this.confirmInfo.conditionUrl + '?message_id=' + this.confirmInfo.messageId },
        { title: '配信内容の確認', url: '' },
      ];
    },
  },
  watch: {
    // props の変化に追従できるようにする保険。このページ内では通常不変だが、
    // 万一 confirmInfo が差し替わっても textarea 高さが崩れないように再調整する。
    scriptTagCode() {
      this.$nextTick(this.adjustSetTextareaHeight);
    },
  },
  // set 用スクリプトは配信設定 (placeholders / useLargeImagePlaceholder) に応じて
  // 行数が大きく変動する。readonly の textarea を内容の実高さにフィットさせるため、
  // マウント時 / scriptTagCode 再計算時に scrollHeight を直接適用する。
  mounted() {
    this.$nextTick(this.adjustSetTextareaHeight);
  },
  methods: {
    // 任意の文字列を JS 文字列リテラルとして安全にエスケープする。
    // - JSON.stringify で標準のエスケープ(", \, 制御文字)を適用
    // - "</" を "<\\/" に置換: コピペ後の HTML 内で外側 <script> が早期終了することを防ぐ
    // - U+2028 / U+2029 は古い JS 仕様で文字列リテラル内の改行扱いとなり SyntaxError を生むため明示的に \\u エスケープする
    toJsLiteral(value) {
      var s = value == null ? '' : String(value);
      return JSON.stringify(s)
        .replace(/<\//g, '<\\/')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
    },
    // set 用 textarea を内容の scrollHeight に合わせて高さ確定させる。
    // v-if="!isAutoTrigger" で非描画のケースでは $refs が取れないためガードする。
    // 一度 height=auto に戻してから scrollHeight を読むことで、縮小方向にも追従できるようにする。
    // ブラウザの border / サブピクセル丸めで 1px スクロールバーが発生するケースを避けるため
    // 余裕として +10px を加算する。
    adjustSetTextareaHeight() {
      var el = this.$refs.setTextarea;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 10 + 'px';
    },
    getScheduleIconUrl(schedule) {
      return schedule.icon_url || this.defaultIconUrl;
    },
    getScheduleBadgeUrl(schedule) {
      return schedule.badge_image_url || this.defaultBadgeUrl;
    },
    placeholderKeyDisplay(ph) {
      return '{{' + ph.placeholder_key + '}}';
    },
    wtToAngle(h, m) {
      return (((h + m / 60) / 24) * 360 - 90) * (Math.PI / 180);
    },
    wtInRange(h) {
      if (!this.hasWorkingTime) return false;
      var sT = this.wtStartHour * 60 + this.wtStartMinute;
      var eT = this.wtEndHour * 60 + this.wtEndMinute;
      var hS = h * 60;
      if (sT <= eT) return hS >= sT && hS < eT;
      return hS >= sT || hS < eT;
    },
    formatMinutesReadable(minutes) {
      var m = parseInt(minutes);
      if (isNaN(m) || m < 0) return '';
      var days = Math.floor(m / 1440);
      var hours = Math.floor((m % 1440) / 60);
      var mins = m % 60;
      var parts = [];
      if (days > 0) parts.push(days + '日');
      if (hours > 0) parts.push(hours + '時間');
      if (mins > 0 || parts.length === 0) parts.push(mins + '分');
      return parts.join(' ');
    },
    replacePlaceholders(text) {
      if (!text) return text;
      var placeholders = this.message.placeholders || [];
      placeholders.forEach(function (ph) {
        if (ph.placeholder_key) {
          var replacement = ph.default_value || '{{' + ph.placeholder_key + '}}';
          text = text.split('{{' + ph.placeholder_key + '}}').join(replacement);
          text = text.split('{' + ph.placeholder_key + '}').join(replacement);
        }
      });
      return text;
    },
    onBackConditionClick() {
      location.href = this.confirmInfo.conditionUrl + '?message_id=' + this.confirmInfo.messageId;
    },
    // 「配信を開始する」ボタン押下 → 実行前に確認モーダルを開く
    onDeliveryClick() {
      if (this.isLoadingSaveBtn || this.isCompleteMessage) return;
      this.isConfirmDelivery = true;
    },
    onCloseConfirmDelivery() {
      this.isConfirmDelivery = false;
    },
    onConfirmDelivery() {
      // 確認モーダル Enter 連打 / 二重発火による配信開始API多重送信を防ぐ
      if (this.isLoadingSaveBtn || this.isCompleteMessage) return;
      this.isConfirmDelivery = false;
      this.onSetDeliveryClick();
    },
    onSetDeliveryClick() {
      // 直接呼ばれた場合の保険ガード (モーダル経由でない呼び出し経路の二重発火を防ぐ)
      if (this.isLoadingSaveBtn || this.isCompleteMessage) return;
      this.isLoadingSaveBtn = true;
      var self = this;

      var params = {
        message_id: this.confirmInfo.messageId,
      };

      axios
        .post('/api/reminder/message/start', params)
        .then(function (res) {
          if (self.handleResponse(res)) {
            self.completeMessage = 'リマインダー配信の設定が完了しました。';
            self.isCompleteMessage = true;
          } else {
            self.isError = true;
            self.errorMessage = res && res.data && res.data.message ? res.data.message : '配信の設定に失敗しました';
          }
          setTimeout(function () {
            self.isLoadingSaveBtn = false;
          }, 300);
        })
        .catch(function (error) {
          self.handleHttpError(error);
          self.isError = true;
          self.errorMessage = '配信の設定に失敗しました';
          setTimeout(function () {
            self.isLoadingSaveBtn = false;
          }, 300);
        });
    },
    // set / unset 用それぞれの textarea に対応できるよう、対象 textarea の name 属性を
    // 第2引数で受け取ってコピー対象を切り替える。未指定時は従来通り set 用をコピーする。
    // 第3引数 copiedTarget で「コピーしました」表示の対象ボタン('set'|'unset')を識別し、
    // クリップボード書き込み成功時のみツールチップを切り替える。
    // (キーボード操作で button が起動された場合や、書き込み失敗時に誤表示するのを防ぐため)
    onClipClick(event, targetName, copiedTarget) {
      event.preventDefault();
      var self = this;
      var name = targetName || 'scriptTagCode';
      var text = $('textarea[name="' + name + '"]').prop('value');
      this.$copyText(text).then(
        function () {
          if (copiedTarget) {
            self.toggleCopyText(copiedTarget);
          }
        },
        function () {
          self.isError = true;
          self.errorMessage = 'クリップボードへのコピーに失敗しました';
        }
      );
    },
    // 引数 target('set' or 'unset') で押されたボタンを識別し、そのボタンのツールチップだけを「コピーしました」に切り替える
    toggleCopyText(target) {
      this.copiedTarget = target;
      var self = this;
      setTimeout(function () {
        // 短時間で別ボタンが押された場合に上書きしない
        if (self.copiedTarget === target) {
          self.copiedTarget = null;
        }
      }, 2000);
    },
    onCloseErrorModal() {
      this.isError = false;
      this.errorMessage = '';
    },
    onCloseControlModal() {
      if (this.isCompleteMessage) {
        location.href = this.confirmInfo.listUrl;
      }
    },
    onCancelControlModal() {
      location.href = this.confirmInfo.listUrl;
    },
  },
};
</script>

<style scoped>
.confirm-preview-header {
  display: flex;
  gap: 20px;
  padding: 20px 20px 8px;
}
.confirm-preview-header__col {
  flex: 1;
  font-weight: 700;
  font-size: 17px;
  text-align: center;
}
.confirm-preview-row {
  display: flex;
  gap: 20px;
  padding: 0 20px;
  align-items: flex-start;
}
.confirm-preview-row__col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.confirm-preview-separator {
  text-align: center;
  color: #999;
  font-size: 13px;
  font-weight: 700;
  padding: 12px 0 4px;
  width: 100%;
}
.confirm-preview-label {
  font-size: 12px;
  font-weight: normal;
  color: #333;
  padding: 0 0 4px 22px;
  align-self: flex-start;
}
.confirm-preview-row--last {
  margin-bottom: 30px;
}
.confirm-nw-clock-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
}
.confirm-nw-clock-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.confirm-nw-clock-time {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}
.confirm-nw-clock-tilde {
  font-size: 14px;
  color: #94a3b8;
  line-height: 1.2;
}
.confirm-nw-clock {
  display: block;
  flex-shrink: 0;
}
.c-title .c-title__text + .c-text {
  margin-left: 12px;
}
.c-title .c-title__text + .c-text a {
  text-decoration: underline;
}
.script-tag-wrapper {
  display: flex;
  flex-direction: column;
}
.script-tag-block {
  width: 100%;
}
/* set / unset の各スクリプトブロックを縦に並べ、間に余白を取る */
.script-tag-block + .script-tag-block {
  margin-top: 28px;
}
.script-tag-heading {
  font-size: 16px;
  font-weight: 600;
  color: #2b3845;
  margin-bottom: 6px;
}
.script-tag-desc {
  font-size: 12px;
  line-height: 1.7em;
  color: #64748b;
  margin-bottom: 10px;
}
.script-tag-desc p {
  margin: 0;
}
.script-tag-desc p + p {
  margin-top: 2px;
}
.script-tag-area {
  display: flex;
  align-items: flex-start;
  width: 100%;
}
.script-tag-textarea {
  flex: 1;
  /* 内容に合わせて scrollHeight を JS で適用するため固定 height は設けない。
       極端に短い / 長いケースで見栄えが崩れないよう min / max だけ抑える。 */
  min-height: 120px;
  max-height: 800px;
  resize: vertical;
}
/* unset 側は 3 行固定なので JS 調整対象外。min/max と競合しないよう明示的に解除する。 */
.script-tag-textarea--unset {
  height: 120px;
  min-height: 0;
  max-height: none;
}
.script-tag-copy-btn {
  flex-shrink: 0;
  margin-left: 8px;
}
.p-message-edit-confirm--preview :deep(.p-message-edit-confirm__title) {
  width: 230px;
}

/* 差し込みテキスト / 差し込み大画像URL 有効時の警告ノート。
   auto はデフォルト値配信の注意喚起、manual は下段スクリプトタグ設置の案内で共通 UI。 */
.placeholder-notice {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 16px;
  background: #fffaeb;
  border: 1px solid #d4941f;
  color: #856404;
  font-size: 14px;
  line-height: 1.6;
}
.placeholder-notice__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #f0ad4e;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  margin-top: 3px;
}
.placeholder-notice__body {
  flex: 1;
  white-space: pre-line;
}
</style>
